<template>
  <b-row>
    <b-col>
      <b-card class="mb-1">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <div class="d-flex align-items-center">
            <b-card-title class="mb-0">
              Product Maturities
            </b-card-title>
            <hint-popup
              :id="37"
              class="ml-50"
            />
          </div>
          <b-button
            v-if="portfolioType !== 'u'"
            variant="outline-secondary"
            size="sm"
            class="d-flex align-items-center"
            @click="downloadCsv"
          >
            <b-img
              :src="require('@/assets/images/icons/file-icons/csv.png')"
              height="20"
              width="20"
            />
            <span class="ml-50 font-weight-bold text-body">Download CSV</span>
          </b-button>
        </div>
        <div class="d-flex align-items-center mb-2">
          <b-form-group class="mb-0 mr-1">
            <b-form-radio-group
              v-model="selectedMode"
              :options="modeOptions"
              name="maturities-mode-selection"
              button-variant="outline-primary"
              buttons
            />
          </b-form-group>
          <span
            class="mr-1 font-small-4"
            :style="{ visibility: selectedMode === 'upcoming' && portfolioType !== 'u' ? 'visible' : 'hidden' }"
          >
            Type
          </span>
          <b-form-group
            class="mb-0"
            :style="{ visibility: selectedMode === 'upcoming' && portfolioType !== 'u' ? 'visible' : 'hidden' }"
          >
            <b-form-radio-group
              v-model="selectedUpcomingType"
              :options="upcomingTypeOptions"
              name="maturities-upcoming-type-selection"
              button-variant="outline-primary"
              buttons
            />
          </b-form-group>
        </div>

        <div class="font-weight-bold mb-1">
          {{ currentCounts ? currentCounts.shown : pagination.totalRows }} results found
          <span
            v-if="currentCounts && currentCounts.restricted > 0"
            class="text-muted font-weight-normal"
          >(further {{ currentCounts.restricted }} results outside your date permission window. The {{ selectedMode === 'past' ? 'earliest' : 'latest' }} date viewable is {{ formatIsoDate(currentDateBoundary) }})</span>
          <br v-if="pagination.totalRows > pagination.perPage">
          <span
            v-if="pagination.totalRows > pagination.perPage"
            class="font-weight-normal"
          >Showing {{ showing[0] }} to {{ showing[1] }} of {{ pagination.totalRows }}</span>
        </div>

        <b-table
          responsive
          hover
          :items="maturitiesData.items"
          :fields="tableFields"
          :busy="pagination.loading"
          :sort-by.sync="pagination.sortBy"
          :sort-desc.sync="pagination.sortDesc"
          :sort-direction="pagination.sortDirection"
          no-local-sorting
          show-empty
          @row-clicked="productClicked"
        >
          <template #table-busy>
            <div class="text-center py-5">
              <b-spinner variant="primary" />
            </div>
          </template>

          <template #cell(productid)="data">
            <span class="font-weight-bold">{{ data.item.productid }}</span>
          </template>

          <template #cell(producttypename)="data">
            <div class="d-flex align-items-center">
              <b-avatar
                class="mr-1"
                :variant="data.item.pticoncolor || 'light-secondary'"
              >
                <feather-icon
                  size="18"
                  :icon="data.item.pticonname || 'ArrowRightIcon'"
                />
              </b-avatar>
              <span>{{ data.item.producttypename }}</span>
            </div>
          </template>

          <template #cell(terminationdate)="data">
            {{ formatIsoDate(data.item.terminationdate) }}
          </template>

          <template #cell(terminationoutcome)="data">
            {{ formatOutcome(data.item.terminationoutcome) }}
          </template>

          <template #cell(notional)="data">
            {{ formatNotional(data.item.notional) }}
          </template>

          <template #cell(maturitypayoff)="data">
            {{ getTerminationValue(data.item) }}
          </template>

          <template #cell(income)="data">
            {{ data.item.income }}
          </template>

          <template #cell(totalpayoff)="data">
            {{ data.item.totalpayoff }}
          </template>

          <template #cell(eventdate)="data">
            {{ formatIsoDate(data.item.eventdate) }}
          </template>

          <template #cell(eventname)="data">
            {{ data.item.eventname }}
          </template>

          <template #cell(level)="data">
            <div class="d-flex align-items-center">
              <feather-icon
                v-if="data.item.status"
                :icon="data.item.status"
                :class="`text-${data.item.color || 'secondary'} mr-50`"
                size="18"
              />
              <span>{{ getLevel(data.item) }}</span>
            </div>
          </template>

          <template #cell(probability)="data">
            {{ getProbability(data.item) }}
          </template>
        </b-table>

        <div
          v-if="maturitiesData.items.length > 0"
          class="d-flex justify-content-between px-2"
        >
          <div class="my-2">
            <span class="pb-1">Rows Per Page</span>
            <b-form-select
              v-model="pagination.perPage"
              :options="pagination.perPageOptions"
              class="pagination-select"
            />
          </div>

          <div class="my-2 d-flex flex-column justify-content-end">
            <b-pagination
              v-model="pagination.currentPage"
              :total-rows="pagination.totalRows"
              :per-page="pagination.perPage"
              align="center"
              size="lg"
              class="mb-0"
            />
          </div>
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardTitle,
  BTable,
  BSpinner,
  BAvatar,
  BFormSelect,
  BPagination,
  BFormGroup,
  BFormRadioGroup,
  BButton,
  BImg,
} from 'bootstrap-vue'
import { formatIsoDate } from '@/utils/formatters'
import HintPopup from '@/views/components/HintPopup.vue'

export default {
  components: {
    HintPopup,
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BTable,
    BSpinner,
    BAvatar,
    BFormSelect,
    BPagination,
    BFormGroup,
    BFormRadioGroup,
    BButton,
    BImg,
  },
  props: {
    productWeightMode: {
      type: String,
      default: 'equal',
    },
    portfolioType: {
      type: [String, Number],
      default: 'u',
    },
    searchFilter: {
      type: Object,
      default: null,
    },
    portfolioCounts: {
      type: Object,
      default: null,
    },
    portfolioName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedMode: 'past',
      maturitiesRequestId: 0,
      modeOptions: [
        { text: 'Past', value: 'past' },
        { text: 'Upcoming', value: 'upcoming' },
      ],
      selectedUpcomingType: 'next',
      upcomingTypeOptions: [
        { text: 'Next', value: 'next' },
        { text: 'Final', value: 'final' },
      ],
      maturitiesData: {
        items: [],
      },
      dateBoundaries: {
        past: null,
        next: null,
        final: null,
      },
      universeCounts: {
        past: null,
        next: null,
      },
      pagination: {
        currentPage: 1,
        perPage: 10,
        totalRows: 0,
        totalPages: 0,
        loading: false,
        error: false,
        sortBy: 'terminationdate',
        sortDesc: false,
        sortDirection: 'asc',
        filter: '',
        perPageOptions: [5, 10, 20, 50, 100],
      },
      pastTableFields: [
        {
          key: 'productid', label: 'ID', thStyle: { width: '5%' }, sortable: true,
        },
        {
          key: 'producttypename', label: 'Type', thStyle: { width: '15%' }, sortable: true,
        },
        {
          key: 'productname', label: 'Product', thStyle: { width: '30%' }, sortable: true,
        },
        {
          key: 'terminationdate', label: 'Termination Date', thStyle: { width: '12%' }, sortable: true,
        },
        {
          key: 'terminationoutcome', label: 'Outcome', thStyle: { width: '10%' }, sortable: true,
        },
        {
          key: 'maturitypayoff', label: 'Termination Value', thStyle: { width: '10%' }, sortable: true,
        },
        {
          key: 'income', label: 'Total Cashflows', thStyle: { width: '10%' }, sortable: true,
        },
        {
          key: 'totalpayoff', label: 'Total Payoff', thStyle: { width: '8%' }, sortable: true,
        },
      ],
      upcomingTableFields: [
        {
          key: 'productid', label: 'ID', thStyle: { width: '5%' }, sortable: true,
        },
        {
          key: 'producttypename', label: 'Type', thStyle: { width: '10%' }, sortable: true,
        },
        {
          key: 'productname', label: 'Product', thStyle: { width: '33%' }, sortable: true,
        },
        {
          key: 'eventdate', label: 'Date', thStyle: { width: '12%' }, sortable: true,
        },
        {
          key: 'eventname', label: 'Event', thStyle: { width: '22%' }, sortable: true,
        },
        {
          key: 'level', label: 'Level', thStyle: { width: '10%' }, sortable: true,
        },
        {
          key: 'probability', label: 'Probability', thStyle: { width: '8%' }, sortable: true,
        },
      ],
    }
  },
  computed: {
    tableFields() {
      const isPast = this.selectedMode === 'past'
      const baseFields = isPast ? this.pastTableFields : this.upcomingTableFields
      if (this.productWeightMode !== 'notional') return baseFields

      const fields = baseFields.map(field => (field.key === 'productname'
        ? { ...field, thStyle: { width: isPast ? '20%' : '23%' } } : field))
      const precedingIndex = fields.findIndex(field => field.key === (isPast ? 'terminationoutcome' : 'productname'))
      fields.splice(precedingIndex + 1, 0, {
        key: 'notional', label: 'Notional', thStyle: { width: '10%' },
      })
      return fields
    },
    showing() {
      const from = (this.pagination.currentPage - 1) * this.pagination.perPage + 1
      const to = Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.totalRows)
      return [from, to]
    },
    currentCounts() {
      if (this.portfolioType === 'u') {
        return this.selectedMode === 'past' ? this.universeCounts.past : this.universeCounts.next
      }
      if (!this.portfolioCounts) return null
      if (this.selectedMode === 'past') return this.portfolioCounts.PASTMATURITIES
      if (this.selectedUpcomingType === 'next') return this.portfolioCounts.NEXTUPCOMINGMATURITIES
      return this.portfolioCounts.FINALUPCOMINGMATURITIES
    },
    currentDateBoundary() {
      if (this.selectedMode === 'past') return this.dateBoundaries.past
      if (this.selectedUpcomingType === 'next') return this.dateBoundaries.next
      return this.dateBoundaries.final
    },
  },
  watch: {
    productWeightMode() {
      this.maturitiesData.items = []
      if (this.pagination.currentPage === 1) {
        this.loadMaturities()
      } else {
        this.pagination.currentPage = 1
      }
    },
    selectedMode(mode) {
      this.pagination.currentPage = 1
      // Swap date sort key to match the new mode's date field
      if (mode === 'past' && this.pagination.sortBy === 'eventdate') {
        this.pagination.sortBy = 'terminationdate'
      } else if (mode === 'upcoming' && this.pagination.sortBy === 'terminationdate') {
        this.pagination.sortBy = 'eventdate'
      }
      this.loadMaturities()
    },
    selectedUpcomingType() {
      if (this.selectedMode === 'upcoming') {
        this.pagination.currentPage = 1
        this.loadMaturities()
      }
    },
    'pagination.currentPage': function onCurrentPageChange() {
      this.loadMaturities()
    },
    'pagination.perPage': function onPerPageChange() {
      if (this.pagination.currentPage === 1) {
        this.loadMaturities()
      } else {
        this.pagination.currentPage = 1
      }
    },
    'pagination.sortBy': function onSortByChange() {
      this.loadMaturities()
    },
    'pagination.sortDesc': function onSortDescChange() {
      this.loadMaturities()
    },
  },
  mounted() {
    this.loadMaturities()
  },
  methods: {
    formatNotional(value) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed.toLocaleString() : String(value)
    },
    formatOutcome(value) {
      if (!value) return ''
      const cleaned = value.replace(/date/gi, '')
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
    },
    getTerminationValue(item) {
      if (item.maturitypayoff && item.maturitypayoff.parsedValue !== undefined) {
        return item.maturitypayoff.parsedValue
      }
      return item.maturitypayoff
    },
    getLevel(item) {
      if (!item.level) return ''
      if (item.level.parsedValue !== undefined) {
        return item.level.parsedValue ? `${item.level.parsedValue}%` : ''
      }
      return item.level ? `${item.level}%` : ''
    },
    getProbability(item) {
      let prob = item.probability
      if (prob && prob.parsedValue !== undefined) {
        prob = prob.parsedValue
      }
      if (prob && parseFloat(prob) !== 0) {
        return `${Math.round(parseFloat(prob) * 100)}%`
      }
      return ''
    },
    loadMaturities() {
      this.maturitiesRequestId += 1
      const requestId = this.maturitiesRequestId
      this.pagination.loading = true
      this.pagination.error = null

      let endpoint
      const params = {
        page: this.pagination.currentPage,
        perpage: this.pagination.perPage,
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc ? 'yes' : 'no',
      }

      if (this.portfolioType === 'u') {
        // Universe portfolio endpoints
        if (this.selectedMode === 'past') {
          endpoint = 'lifecycle/public/report.cfc?method=getUniversePortfolioPastMaturities'
        } else {
          endpoint = 'lifecycle/public/report.cfc?method=getUniversePortfolioNextMaturities'
        }
      } else if (this.portfolioType === 'v') {
        // Virtual portfolio endpoints
        if (this.selectedMode === 'past') {
          endpoint = 'lifecycle/public/report.cfc?method=getVirtualPortfolioPastMaturities'
        } else if (this.selectedUpcomingType === 'next') {
          endpoint = 'lifecycle/public/report.cfc?method=getVirtualPortfolioNextMaturities'
        } else {
          endpoint = 'lifecycle/public/report.cfc?method=getVirtualPortfolioFinalMaturities'
        }
        params.searchfilter = JSON.stringify(this.searchFilter)
      } else {
        // User portfolio endpoints
        if (this.selectedMode === 'past') {
          endpoint = 'lifecycle/public/report.cfc?method=getUserPortfolioPastMaturities'
        } else if (this.selectedUpcomingType === 'next') {
          endpoint = 'lifecycle/public/report.cfc?method=getUserPortfolioNextMaturities'
        } else {
          endpoint = 'lifecycle/public/report.cfc?method=getUserPortfolioFinalMaturities'
        }
        params.portfolioid = this.portfolioType
        params.productweightingmode = this.productWeightMode
      }

      this.$http.get(endpoint, { params })
        .then(response => {
          if (requestId !== this.maturitiesRequestId) return
          this.maturitiesData.items = response.data.DATA || []
          this.pagination.totalRows = response.data.INFO?.NUMROWS || 0
          this.pagination.totalPages = response.data.INFO?.NUMPAGES || 0
          this.pagination.currentPage = response.data.INFO?.CURRENTPAGE?.parsedValue || response.data.INFO?.CURRENTPAGE || 1
          this.pagination.perPage = response.data.INFO?.PERPAGE?.parsedValue || response.data.INFO?.PERPAGE || 10
          const boundary = response.data.INFO?.DATEBOUNDARY || null
          if (this.selectedMode === 'past') {
            this.dateBoundaries.past = boundary
            if (this.portfolioType === 'u') this.universeCounts.past = response.data.INFO?.COUNTS || null
          } else if (this.selectedUpcomingType === 'next') {
            this.dateBoundaries.next = boundary
            if (this.portfolioType === 'u') this.universeCounts.next = response.data.INFO?.COUNTS || null
          } else {
            this.dateBoundaries.final = boundary
          }
        })
        .catch(err => {
          if (requestId !== this.maturitiesRequestId) return
          console.error('Error loading maturities:', err)
          this.pagination.error = true
        })
        .finally(() => {
          if (requestId !== this.maturitiesRequestId) return
          this.pagination.loading = false
        })
    },
    productClicked(item) {
      if (!item.productid) return
      this.$router.push({ name: 'product-report', params: { reportNumber: item.productid } })
    },
    downloadCsv() {
      this.$emit('download-csv')

      let method
      let params
      const isUniverse = this.portfolioType === 'u'
      const isVirtual = this.portfolioType === 'v'
      if (isUniverse) {
        method = this.selectedMode === 'past' ? 'downloadCSVUniversePastMaturities' : 'downloadCSVUniverseNextMaturities'
        params = {
          sortBy: this.pagination.sortBy,
          sortDesc: this.pagination.sortDesc ? 'yes' : 'no',
        }
      } else if (isVirtual) {
        if (this.selectedMode === 'past') {
          method = 'downloadCSVVirtualPortfolioPastMaturities'
        } else if (this.selectedUpcomingType === 'next') {
          method = 'downloadCSVVirtualPortfolioNextMaturities'
        } else {
          method = 'downloadCSVVirtualPortfolioFinalMaturities'
        }
        params = {
          searchfilter: JSON.stringify(this.searchFilter),
          sortBy: this.pagination.sortBy,
          sortDesc: this.pagination.sortDesc ? 'yes' : 'no',
        }
      } else {
        if (this.selectedMode === 'past') {
          method = 'downloadCSVUserPortfolioPastMaturities'
        } else if (this.selectedUpcomingType === 'next') {
          method = 'downloadCSVUserPortfolioNextMaturities'
        } else {
          method = 'downloadCSVUserPortfolioFinalMaturities'
        }
        params = {
          portfolioid: this.portfolioType,
          portfolioname: this.portfolioName,
          productweightingmode: this.productWeightMode,
          sortBy: this.pagination.sortBy,
          sortDesc: this.pagination.sortDesc ? 'yes' : 'no',
        }
      }
      this.$http.get(`lifecycle/public/report.cfc?method=${method}`, { params, responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(response.data)
          const a = document.createElement('a')
          a.style.display = 'none'
          a.href = url
          // eslint-disable-next-line prefer-destructuring
          a.download = response.headers['content-disposition'].match(/"([^']+)"/)[1]
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
        })
    },
    formatIsoDate,
  },
}
</script>

<style lang="scss" scoped>
.pagination-select {
  width: 90px;
  margin-left: 0.5rem;
}
</style>
